import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import type { HomeContent } from './contentModel';

/** A proxied image path as the backend writes it into the content. */
const IMAGE_PATH = /^images\/[0-9a-f]{16}$/;

export interface HomeContentApi {
  /** The News & updates content; post images are `images/<id>` paths. */
  getContent(): Promise<HomeContent>;
  /**
   * One proxied image as a `data:` URL. An `<img src>` cannot carry the
   * Backstage token, and the default CSP already allows `data:` images.
   */
  getImage(path: string): Promise<string>;
}

export const homeContentApiRef = createApiRef<HomeContentApi>({
  id: 'app.home-content',
});

export class HomeContentClient implements HomeContentApi {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  async getContent(): Promise<HomeContent> {
    return (await this.get('content')).json();
  }

  async getImage(path: string): Promise<string> {
    // Only the backend's own image paths, so the token never goes elsewhere.
    if (!IMAGE_PATH.test(path)) throw new Error('Not a homepage image path');
    const blob = await (await this.get(path)).blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private async get(path: string): Promise<Response> {
    const baseUrl = await this.deps.discoveryApi.getBaseUrl('home-content');
    const response = await this.deps.fetchApi.fetch(`${baseUrl}/${path}`);
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return response;
  }
}
